import {
  createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { authClient, type AuthProvider, type AuthUser } from '../../lib/auth.js';
import { meKeys, mergeFrom } from '../../lib/me.js';

export interface LinkResult {
  readonly ok: boolean;
  /** `unavailable` = o browser recusou a janela; `cancelled` = fechou-se. */
  readonly reason?: 'cancelled' | 'unavailable' | 'credential_in_use';
  readonly detail?: string;
}

interface AccountValue {
  readonly user: AuthUser | null;
  readonly ready: boolean;
  readonly kind: 'firebase' | 'local';
  link(provider: AuthProvider, email?: string): Promise<LinkResult>;
  signOut(): Promise<void>;
}

const Ctx = createContext<AccountValue | null>(null);

/**
 * [ADR-008] — entra sozinha na primeira visita.
 *
 * Ninguém carrega em "criar conta" para ler o acervo. A sessão anónima é
 * criada em silêncio, o progresso começa a acumular, e ligar conta fica para
 * quando houver alguma coisa a proteger.
 */
export function AccountProvider({ children }: { readonly children: ReactNode }) {
  const client = authClient();
  const queryClient = useQueryClient();
  const [user, setUser] = useState<AuthUser | null>(client.current());
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const off = client.subscribe(setUser);
    void client.ensureSignedIn()
      .then(setUser)
      .catch(() => { /* sem sessão a app continua legível; só não guarda nada */ })
      .finally(() => setReady(true));
    return off;
  }, [client]);

  const link = useCallback(async (provider: AuthProvider, email?: string) => {
    const outcome = await client.link(provider, email);
    if (outcome.ok) {
      setUser(outcome.user);
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      return { ok: true };
    }
    if (outcome.reason === 'credential_in_use') {
      /**
       * §47 — o caso que se perde sem isto.
       *
       * `linkWithCredential` mantém o uid, por isso o caminho normal não migra
       * nada. Este é o outro: a conta já existia, o Firebase recusa a ligação,
       * e o trabalho da sessão anónima ficaria órfão. O token antigo vai para
       * o servidor fundir antes de desaparecer.
       */
      await mergeFrom(outcome.previousToken);
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      setUser(client.current());
      return { ok: true, detail: 'merged' };
    }
    // O `reason` sobe. Sem ele a interface nao consegue distinguir um popup
    // bloqueado de alguem que fechou a janela, e acabava a nao dizer nada.
    return {
      ok: false,
      reason: outcome.reason,
      ...(outcome.detail === undefined ? {} : { detail: outcome.detail }),
    };
  }, [client, queryClient]);

  const signOut = useCallback(async () => {
    await client.signOut();
    queryClient.removeQueries({ queryKey: ['me'] });
    setUser(null);
    await client.ensureSignedIn().then(setUser);
  }, [client, queryClient]);

  const value = useMemo<AccountValue>(
    () => ({ user, ready, kind: client.kind, link, signOut }),
    [user, ready, client.kind, link, signOut],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAccount(): AccountValue {
  const value = useContext(Ctx);
  if (value === null) {
    // Uma página de conta fora do provider seria um ecrã vazio sem explicação.
    throw new Error('useAccount fora de <AccountProvider>');
  }
  return value;
}

/**
 * A mesma coisa, sem exigir o provider.
 *
 * Para uma página de conta, estar fora do provider é um bug e o `useAccount`
 * grita. Para um extra opcional numa página de conteúdo — o painel de
 * perguntas — o comportamento certo é não aparecer, e não derrubar a página
 * inteira que existia perfeitamente sem ele.
 */
export function useOptionalAccount(): AccountValue | null {
  return useContext(Ctx);
}

/** Queries de utilizador só correm quando há sessão. */
export const useMeEnabled = (): boolean => useAccount().user !== null;

export { meKeys };
